import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@components/common/Button';
import { Input } from '@components/common/Input';
import { useCreateUser, useUpdateUser } from '@api/hooks/useUsers';
import type { AdminUser, CreateUserRequest, UpdateUserRequest } from '@schemas/api';

const baseUserSchema = z.object({
  email: z.string().email('Invalid email address'),
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  is_admin: z.boolean().optional(),
});

const createUserSchema = baseUserSchema.extend({
  password: z.string().min(8, 'Password must be at least 8 characters').max(128, 'Password must be 128 characters or fewer'),
});

const updateUserSchema = baseUserSchema.extend({
  password: z.string().optional(),
});

type UserFormData = z.infer<typeof updateUserSchema>;

interface UserFormProps {
  user?: AdminUser;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const UserForm = ({ user, onSuccess, onCancel }: UserFormProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<UserFormData>({
    resolver: zodResolver(user ? updateUserSchema : createUserSchema),
    defaultValues: user
      ? {
          email: user.email,
          first_name: user.first_name,
          last_name: user.last_name,
          is_admin: user.is_admin,
        }
      : undefined,
  });

  const createUserMutation = useCreateUser();
  const updateUserMutation = useUpdateUser();

  const onSubmit = async (data: UserFormData) => {
    try {
      if (user) {
        const updatePayload: UpdateUserRequest = {
          email: data.email,
          first_name: data.first_name,
          last_name: data.last_name,
          is_admin: data.is_admin,
        };
        await updateUserMutation.mutateAsync({
          userId: user.id,
          data: updatePayload,
        });
      } else {
        if (!data.password) {
          return;
        }
        const createPayload: CreateUserRequest = {
          email: data.email,
          first_name: data.first_name,
          last_name: data.last_name,
          password: data.password,
          is_admin: data.is_admin || false,
        };
        await createUserMutation.mutateAsync(createPayload);
      }
      onSuccess?.();
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const isLoading = createUserMutation.isPending || updateUserMutation.isPending;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Input
        label="Email"
        type="email"
        {...register('email')}
        error={errors.email?.message}
      />

      <Input
        label="First Name"
        {...register('first_name')}
        error={errors.first_name?.message}
      />

      <Input
        label="Last Name"
        {...register('last_name')}
        error={errors.last_name?.message}
      />

      {!user && (
        <Input
          label="Password"
          type="password"
          placeholder="Set an initial password"
          {...register('password')}
          error={errors.password?.message}
        />
      )}

      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          {...register('is_admin')}
          className="w-4 h-4 rounded border-gray-300 dark:border-gray-600"
        />
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Admin User
        </span>
      </label>

      <div className="flex gap-2 pt-4">
        <Button
          type="submit"
          variant="primary"
          disabled={isLoading}
          loading={isLoading}
        >
          {user ? 'Update User' : 'Create User'}
        </Button>
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
};
